import { useState, useEffect, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useProviderScope } from '../../auth/useProviderScope';
import type { LookupMaps } from './useAppointmentsData';

// ─── Types ───

export interface FiltersState {
  quickFilter: 'today' | 'this-week' | 'this-month' | '';
  location: string;
  provider: string;
  category: string;
  status: string;
  service: string;
  room: string;
}

const DEFAULT_FILTERS: FiltersState = {
  quickFilter: 'this-month',
  location: '',
  provider: '',
  category: '',
  status: '',
  service: '',
  room: '',
};

// ─── Styles ───

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  lineHeight: 'var(--line-height-sm)',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  display: 'block',
  marginBottom: 'var(--sp-2)',
};

const inputBaseStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  lineHeight: 'var(--line-height-base)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  boxSizing: 'border-box' as const,
  width: '100%',
};

const selectStyle: CSSProperties = {
  ...inputBaseStyle,
  cursor: 'pointer',
  appearance: 'auto',
};

// ─── Quick Filter Pill ───

function QuickFilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 32,
        padding: '0 var(--sp-3)',
        borderRadius: 'var(--radius-full)',
        border: active ? '1.5px solid var(--color-primary)' : '1px solid var(--border)',
        background: active ? 'var(--color-primary-bg, rgba(59,130,246,0.08))' : 'transparent',
        color: active ? 'var(--color-primary)' : 'var(--text-muted)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

// ─── Filters Drawer ───

export interface VisibleFilters {
  category: boolean;
  status: boolean;
  provider: boolean;
  service: boolean;
  room: boolean;
  location: boolean;
}

interface FiltersDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  filters: FiltersState;
  onApply: (filters: FiltersState) => void;
  visibleFilters?: VisibleFilters;
  lookups?: LookupMaps;
}

export function FiltersDrawer({
  open,
  onOpenChange,
  brandTokens,
  filters,
  onApply,
  visibleFilters,
  lookups,
}: FiltersDrawerProps) {
  const [form, setForm] = useState<FiltersState>(filters);
  // PRD.293 — doctors are scoped to their own provider at the query layer, so the
  // provider filter is hidden and never applied for them.
  const providerScope = useProviderScope();

  useEffect(() => {
    if (open) {
      // Clear values for hidden filters so they don't silently apply
      const cleaned = { ...filters };
      if (visibleFilters) {
        if (!visibleFilters.category) cleaned.category = '';
        if (!visibleFilters.status) cleaned.status = '';
        if (!visibleFilters.provider) cleaned.provider = '';
        if (!visibleFilters.service) cleaned.service = '';
        if (!visibleFilters.room) cleaned.room = '';
        if (!visibleFilters.location) cleaned.location = '';
      }
      if (providerScope.scoped) cleaned.provider = '';
      setForm(cleaned);
    }
  }, [open, filters, visibleFilters, providerScope.scoped]);

  const update = <K extends keyof FiltersState>(key: K, value: FiltersState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearAll = () => {
    setForm({ ...DEFAULT_FILTERS, quickFilter: '' });
  };

  const handleApply = () => {
    onApply(form);
    onOpenChange(false);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Filters"
      portalStyle={brandTokens as CSSProperties}
      badges={
        <button
          type="button"
          onClick={handleClearAll}
          style={{
            background: 'transparent',
            color: 'var(--color-primary)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            border: 'none',
            padding: 0,
          }}
        >
          Clear All
        </button>
      }
      footer={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={handleApply}>
            Apply Filters
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5, 20px)',
        }}
      >
        {/* Quick Filters */}
        <div>
          <label style={labelStyle}>Quick Filters</label>
          <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <QuickFilterPill
              label="Today"
              active={form.quickFilter === 'today'}
              onClick={() => update('quickFilter', form.quickFilter === 'today' ? '' : 'today')}
            />
            <QuickFilterPill
              label="This Week"
              active={form.quickFilter === 'this-week'}
              onClick={() =>
                update('quickFilter', form.quickFilter === 'this-week' ? '' : 'this-week')
              }
            />
            <QuickFilterPill
              label="This Month"
              active={form.quickFilter === 'this-month'}
              onClick={() =>
                update('quickFilter', form.quickFilter === 'this-month' ? '' : 'this-month')
              }
            />
          </div>
        </div>

        {/* Location / Clinic */}
        {(!visibleFilters || visibleFilters.location) && (
          <div>
            <label style={labelStyle}>Location / Clinic</label>
            <input
              type="text"
              placeholder="Search location..."
              value={form.location}
              onChange={(e) => update('location', e.target.value)}
              style={inputBaseStyle}
            />
          </div>
        )}

        {/* Provider — hidden for scoped doctors (PRD.293) */}
        {!providerScope.scoped && (!visibleFilters || visibleFilters.provider) && (
          <div>
            <label style={labelStyle}>Provider</label>
            <select
              value={form.provider}
              onChange={(e) => update('provider', e.target.value)}
              style={selectStyle}
            >
              <option value="">All Providers</option>
              {lookups?.providerList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name || p.full_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Category */}
        {(!visibleFilters || visibleFilters.category) && (
          <div>
            <label style={labelStyle}>Category</label>
            <select
              value={form.category}
              onChange={(e) => update('category', e.target.value)}
              style={selectStyle}
            >
              <option value="">All Categories</option>
              {lookups?.categoryList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Status */}
        {(!visibleFilters || visibleFilters.status) && (
          <div>
            <label style={labelStyle}>Status</label>
            <select
              value={form.status}
              onChange={(e) => update('status', e.target.value)}
              style={selectStyle}
            >
              <option value="">All Statuses</option>
              {lookups?.statusList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Services */}
        {(!visibleFilters || visibleFilters.service) && (
          <div>
            <label style={labelStyle}>Services</label>
            <select
              value={form.service}
              onChange={(e) => update('service', e.target.value)}
              style={selectStyle}
            >
              <option value="">All Services</option>
              {lookups?.serviceList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Room (Optional) */}
        {(!visibleFilters || visibleFilters.room) && (
          <div>
            <label style={labelStyle}>Room (Optional)</label>
            <input
              type="text"
              placeholder="Search room..."
              value={form.room}
              onChange={(e) => update('room', e.target.value)}
              style={inputBaseStyle}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
}
